import React from "react";
import clsx from "clsx";
import Tooltip from "antd/lib/tooltip";
import useDelayedValue from "hooks/useDelayedValue";
import { Position } from "./types";
import styles from "./Indicator.module.css";

type IndicatorProps = {
    position: number;
    delay: number;
    color?: number;
    flipped?: boolean;
    inverse?: boolean;
    text: string;
    partition: number;
    offset: number;
    group: string;
    type: "source" | "target";
};

const Indicator = ({
    position,
    delay,
    color,
    flipped = false,
    inverse = false,
    text,
    partition,
    offset,
    group,
    type,
}: IndicatorProps) => {
    const delayedPosition = useDelayedValue(position, delay, -1);
    const delayedOffset = useDelayedValue(offset, delay, -1);

    const ref = React.useRef<HTMLDivElement>(null);
    const [translate, setTranslate] = React.useState(true);
    const [originOffset, setOriginOffset] = React.useState<Position>();

    React.useEffect(() => {
        if (type === "source") return;
        if (!ref.current) return;

        const originElement = document.getElementById(
            `offset-${partition}-${group}-source`
        );

        if (!originElement) {
            setOriginOffset([0, 0]);
            return;
        }

        const originRect = originElement.getBoundingClientRect();
        const messageRect = ref.current.getBoundingClientRect();

        const newOriginOffset: Position = [
            originRect.x - messageRect.x,
            originRect.y - messageRect.y,
        ];

        setOriginOffset(newOriginOffset);
    }, [type, delayedOffset, partition, group]);

    React.useEffect(() => {
        setTranslate(true);
        let raf = requestAnimationFrame(() => {
            raf = requestAnimationFrame(() => {
                setTranslate(false);
            });
        });

        return () => cancelAnimationFrame(raf);
    }, [originOffset]);

    const renderContent = () => (
        <div
            ref={ref}
            id={`offset-${partition}-${group}-${type}`}
            className={clsx(styles.root, {
                [styles.invisible]: type === "source" || delayedPosition < 0,
                [styles.animate]: !translate,
                [styles.flipped]: flipped,
            })}
            style={{
                left: `calc(-60px * ${delayedPosition})`,
                transform:
                    translate && originOffset
                        ? `translate(${originOffset[0]}px,${originOffset[1]}px)`
                        : undefined,
            }}
        >
            <div
                className={clsx(styles.label, {
                    [styles.inverse]: inverse,
                })}
                style={{
                    [inverse
                        ? "backgroundColor"
                        : "color"]: `#${color?.toString(16)}`,
                    borderColor: `#${color?.toString(16)}`,
                }}
            >
                {text}
            </div>
        </div>
    );

    if (type === "target")
        return (
            <Tooltip title="Offset" placement="bottom">
                {renderContent()}
            </Tooltip>
        );

    return renderContent();
};

export default Indicator;
